import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAtom, useSetAtom } from 'jotai';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  useGetAnalysisStructureLocationsQuery,
  useGetStructureDataQuery,
  useGetStructureDefaultAssemblyQuery,
  usePaintRegionGroupsQuery,
  useGetPaintRegionStatusLabelsQuery,
  useGetPoiStatusLabelsQuery,
  useGetWorkpackStatusLabelsQuery,
} from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { useLocalStorage } from '@/hooks';
import { LocationsData } from '@/types';
import { sizeOfAnObjectInMb } from '@/utils/sizeOfAnObject';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_DATA_LOADER_LOCATION_ERROR,
  INSIGHTS_DATA_LOADER_RISK_ASSESSMENT_ERROR,
} from '@/constants/Errors';
import { useSelectedAssemblyTemplateId } from '../../hooks';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const InsightsDataLoader = ({ ...props }) => {
  const structureId = useGetStructureId();
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const [structureLocations, setStructureLocations] = useAtom(state.structureLocations);
  const setstructurePipes = useSetAtom(state.structurePipesByStructureId);
  const setPoiSummaryChartsConfigByStructureId = useSetAtom(state.poiSummaryChartsConfigByStructureId);
  const setHasRiskAssesment = useSetAtom(state.hasRiskAssessment);
  const setSelectedPaintRegionGroupId = useSetAtom(state.selectedPaintRegionGroupId);
  const setStructureBlisterHeightType = useSetAtom(state.structureBlisterHeightType);
  const setPaintRegionStatusLabels = useSetAtom(state.paintRegionStatusLabels);
  const setPoiStatusLabels = useSetAtom(state.poiStatusLabels);
  const setWorkpackStatusLabels = useSetAtom(state.workpackStatusLabels);
  const setCustomBlocksEnabled = useSetAtom(state.customBlocksEnabled);
  const setCustomBlocksNames = useSetAtom(state.customBlocksNames);
  const setAllPaintRegionGroups = useSetAtom(state.allPaintRegionGroups);
  const setStructureDefaultAssemblyId = useSetAtom(state.structureDefaultAssemblyId);
  const [locations, setLocations] = useLocalStorage<LocationsData | undefined>(
    `locations`,
    undefined
  );

  usePaintRegionGroupsQuery({
    variables: {
      input: { structureId, templateId: selectedAssemblyTemplateId || '' },
    },
    skip: !selectedAssemblyTemplateId || !structureId,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    onCompleted: (response) => {
      // if paint region groups are found, enable custom blocks
      const paintGroupsExist = response?.paintRegionGroups.length;
      if (paintGroupsExist) {
        setCustomBlocksEnabled(true);
        setCustomBlocksNames(response?.paintRegionGroups?.map((item) => item.name) ?? []);
      }
      setAllPaintRegionGroups(response?.paintRegionGroups || []);
      const defaultPaintGroupId = response?.paintRegionGroups?.find((item) => item.isDefault)?.id;
      setSelectedPaintRegionGroupId(defaultPaintGroupId);
    },
  });

  useGetStructureDefaultAssemblyQuery({
    variables: { structureId },
    onCompleted: (response) => {
      const id = response?.structure?.defaultAssemblyId;
      setStructureDefaultAssemblyId(id || undefined);
    },
  });

  const { data, error: locationsError } = useGetAnalysisStructureLocationsQuery({
    variables: {
      structureId,
    },
    skip: !!locations?.[structureId],
  });

  const { data: structureQueryResult, error: structureDataError } = useGetStructureDataQuery({
    variables: {
      structureId,
    },
  });

  useEffect(() => {
    const structurePipes =
      structureQueryResult?.structure?.structurePipesConfig?.imperialSteelPipes;
    if (structureId && structurePipes) {
      setstructurePipes(() => ({
        [structureId]: structurePipes as Record<string, number>,
      }));
    }
  }, [
    setstructurePipes,
    structureId,
    structureQueryResult?.structure?.structurePipesConfig?.imperialSteelPipes,
  ]);

  useEffect(() => {
    const config = structureQueryResult?.structure?.reportConfig?.poiSummaryChartsConfig;
    if (!structureId) return;
    if (config === undefined) return;
    setPoiSummaryChartsConfigByStructureId((prev) => ({
      ...prev,
      [structureId]: (config ?? {}) as Record<string, string[] | null | undefined>,
    }));
  }, [setPoiSummaryChartsConfigByStructureId, structureId, structureQueryResult?.structure?.reportConfig?.poiSummaryChartsConfig]);

  useEffect(() => {
    if (locationsError) {
      ErrorLogger.log(
        INSIGHTS_DATA_LOADER_LOCATION_ERROR.withContext({ structureId }).withError(locationsError)
      );
    }

    if (data?.allLocations) {
      if (sizeOfAnObjectInMb(data.allLocations) <= 2.5) {
        setLocations({
          [structureId]: data.allLocations,
        });
        return;
      }
      setStructureLocations(data.allLocations);
    }
  }, [
    data?.allLocations,
    setLocations,
    structureId,
    setStructureLocations,
    structureLocations,
    locationsError,
  ]);

  useEffect(() => {
    if (locations?.[structureId] && !structureLocations)
      setStructureLocations(locations?.[structureId]);
  }, [locations, setStructureLocations, structureId, structureLocations]);

  const { data: statusLabelsData } = useGetPaintRegionStatusLabelsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const { data: poiStatusLabelsData } = useGetPoiStatusLabelsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const { data: workpackStatusLabelsData } = useGetWorkpackStatusLabelsQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (structureDataError) {
      ErrorLogger.log(
        INSIGHTS_DATA_LOADER_RISK_ASSESSMENT_ERROR.withContext({ structureId }).withError(
          structureDataError
        )
      );
    }
    setHasRiskAssesment(structureQueryResult?.structure?.hasRiskAssessment ?? false);
    setStructureBlisterHeightType(structureQueryResult?.structure?.blisterHeightType ?? undefined);
  }, [
    setHasRiskAssesment,
    structureQueryResult?.structure?.hasRiskAssessment,
    structureId,
    structureDataError,
    structureQueryResult?.structure?.blisterHeightType,
    setStructureBlisterHeightType,
  ]);

  useEffect(() => {
    setPaintRegionStatusLabels(statusLabelsData?.setting ?? undefined);
  }, [statusLabelsData?.setting, setPaintRegionStatusLabels]);

  useEffect(() => {
    setPoiStatusLabels(poiStatusLabelsData?.setting ?? undefined);
  }, [poiStatusLabelsData?.setting, setPoiStatusLabels]);

  useEffect(() => {
    setWorkpackStatusLabels(workpackStatusLabelsData?.setting ?? undefined);
  }, [workpackStatusLabelsData?.setting, setWorkpackStatusLabels]);

  return <Wrapper {...props} />;
};
